import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from '../../../theme';
import { Pressable, Text, View, ScrollView, TextInput, Platform } from "react-native";
// Only import createPortal on web at runtime to avoid native bundler issues
let createPortal: ((node: React.ReactNode, container: Element) => React.ReactPortal) | null = null;
if (typeof window !== "undefined" && Platform.OS === "web") {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  createPortal = require("react-dom").createPortal;
}
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import styles from "../SubmitTicketScreenStyles";

type Props = {
  id?: string;
  label: string;
  value: string;
  options: readonly string[] | string[];
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  errorText?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  multiSelect?: boolean;
  /**
   * Extra searchable terms per option value.
   * When provided, the search filter matches against both the option label and
   * any strings in this record. E.g. { "Ada OK": ["1234", "5678"] } allows
   * searching by location ID or SAP ID in addition to the display name.
   */
  optionSearchKeys?: Record<string, string[]>;
  /**
   * Optional display labels per option value.
   * Example: { "Anchorage AK": "Anchorage AK (1010)" }
   */
  optionLabelMap?: Record<string, string>;
  activeDropdownId?: string | null;
  onDropdownToggle?: (id: string | null) => void;
  onBlur?: () => void;
  onChange: (value: string) => void;
};

const SelectField: React.FC<Props> = ({
  id,
  label,
  value,
  options,
  placeholder = "Select...",
  required,
  disabled,
  helperText,
  errorText,
  searchable = false,
  searchPlaceholder = "Search...",
  multiSelect = false,
  optionSearchKeys,
  optionLabelMap,
  activeDropdownId,
  onDropdownToggle,
  onBlur,
  onChange,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  // Web dropdown menu is portaled into document.body with inline colours, so it is
  // not covered by the stylesheet sweep; the global Dark rules also force white text
  // fill on it. Pick explicit dark surface/text colours and opt text out of the
  // white-fill rule via data-lh-keep-color.
  const menuBg = isDark ? colors.background.elevated : "#ffffff";
  const menuBorder = isDark ? colors.border.default : "#d0d0d0";
  const menuDivider = isDark ? colors.border.divider : "#ebebeb";
  const optionText = isDark ? colors.text.primary : "#2d2d2d";
  const optionTextSelected = isDark ? colors.brand.primary : "#005cbc";
  const optionBgSelected = isDark ? colors.background.surface : "#eff5fc";
  const keepColorAttr = isDark ? { "data-lh-keep-color": "1" } : {};
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<any>(null);
  const portalMenuRef = useRef<HTMLElement | null>(null);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const isMobileControlled = Platform.OS !== "web" && Boolean(id) && Boolean(onDropdownToggle);
  const isOpen = isMobileControlled ? activeDropdownId === id : open;
  const prevIsOpenRef = useRef(false);

  // Parse multi-select values
  const selectedValues = useMemo(() => {
    if (!multiSelect || !value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [value, multiSelect]);

  const hasValue = useMemo(() => {
    return multiSelect ? selectedValues.length > 0 : Boolean(value);
  }, [value, multiSelect, selectedValues]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return options;
    }
    const q = searchQuery.trim().toLowerCase();
    return options.filter((option) => {
      const optionLabel = optionLabelMap?.[option] ?? option;
      if (optionLabel.toLowerCase().includes(q)) return true;
      if (optionSearchKeys) {
        const extras = optionSearchKeys[option];
        if (extras?.some((term) => term.toLowerCase().includes(q))) return true;
      }
      return false;
    });
  }, [options, searchable, searchQuery, optionSearchKeys, optionLabelMap]);

  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    if (wasOpen && !isOpen) {
      onBlur?.();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, onBlur]);

  useEffect(() => {
    if (!isOpen || Platform.OS !== "web") {
      return;
    }

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const container = containerRef.current as HTMLElement | null;
      const menu = portalMenuRef.current;
      const target = event.target as Node | null;
      if (!target) return;
      // If click is inside the trigger container or inside the portal menu, ignore
      if (container && container.contains(target)) return;
      if (menu && menu.contains(target)) return;
      setOpen(false);
      setSearchQuery("");
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && searchQuery) {
      setSearchQuery("");
    }
  }, [isOpen, searchQuery]);

  const closeDropdown = () => {
    if (isMobileControlled && onDropdownToggle) {
      onDropdownToggle(null);
    } else {
      setOpen(false);
    }
    setSearchQuery("");
  };

  const toggleDropdown = () => {
    if (isMobileControlled && onDropdownToggle && id) {
      onDropdownToggle(isOpen ? null : id);
      if (isOpen) {
        setSearchQuery("");
      }
      return;
    }

    setOpen((prev) => {
      const nextOpen = !prev;
      if (nextOpen) {
        // When opening on web, capture trigger bounding rect for portal positioning
        if (Platform.OS === "web") {
          try {
            const el = containerRef.current as HTMLElement | null;
            const rect = el?.getBoundingClientRect?.();
            if (rect) setTriggerRect(rect);
          } catch (e) {}
        }
      } else {
        setSearchQuery("");
        setTriggerRect(null);
      }
      return nextOpen;
    });
  };

  const handleOptionPress = (option: string) => {
    if (multiSelect) {
      const newSelected = selectedValues.includes(option)
        ? selectedValues.filter((v) => v !== option)
        : [...selectedValues, option];
      onChange(JSON.stringify(newSelected));
    } else {
      onChange(option);
      closeDropdown();
    }
  };

  // Dark-mode overrides for native (mobile) elements — web portals already use
  // inline colour variables set above; these only apply to the RN View/Text/Pressable tree.
  const darkTrigger = isDark ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default } : null;
  const darkTriggerText = isDark ? { color: colors.text.primary } : null;
  const darkPlaceholderText = isDark ? { color: colors.text.disabled } : null;
  const darkLabel = isDark ? { color: colors.text.secondary } : null;
  const darkMenu = isDark ? { backgroundColor: colors.background.elevated, borderColor: colors.border.default } : null;
  const darkMenuItem = isDark ? { borderBottomColor: colors.border.divider } : null;
  const darkMenuItemActive = isDark ? { backgroundColor: colors.background.surface } : null;
  const darkMenuItemText = isDark ? { color: colors.text.primary } : null;
  const darkMenuItemTextActive = isDark ? { color: colors.brand.primary } : null;
  const darkHelperText = isDark ? { color: colors.text.disabled } : null;

  return (
    <View ref={containerRef} style={[styles.fieldGroup, isOpen ? styles.selectFieldOpen : null]}>
      <Text style={[styles.label, darkLabel]}>
        {label} {required ? <Text style={styles.required}>*</Text> : null}
      </Text>

      <View style={styles.selectWrap}>
        <Pressable
          disabled={disabled}
          onPress={toggleDropdown}
          style={({ pressed }) => [
            styles.selectTrigger,
            darkTrigger,
            disabled ? styles.selectTriggerDisabled : null,
            isDark && disabled ? { backgroundColor: colors.background.surface, borderColor: colors.border.default } : null,
            isOpen ? styles.selectTriggerOpen : null,
            pressed && !disabled ? styles.selectTriggerPressed : null,
          ]}
        >
          <Text
            style={[
              styles.selectTriggerText,
              darkTriggerText,
              !hasValue ? styles.selectPlaceholderText : null,
              !hasValue && isDark ? darkPlaceholderText : null,
              disabled ? styles.selectTriggerTextDisabled : null,
              isDark && disabled ? { color: colors.text.disabled } : null,
            ]}
          >
            {multiSelect && hasValue
              ? `${selectedValues.length} selected`
              : hasValue
              ? (optionLabelMap?.[value] ?? value)
              : placeholder}
          </Text>
          <MaterialIcons
            name={isOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
            size={20}
            color={disabled ? (isDark ? colors.text.disabled : "#a8a8a8") : (isDark ? colors.text.secondary : "#585858")}
          />
        </Pressable>

        {isOpen && !disabled ? (
          // Render menu into portal on web for guaranteed overlay, otherwise inline
          Platform.OS === "web" && createPortal && triggerRect ? (
            createPortal(
              <div
                ref={(el: HTMLElement | null) => { portalMenuRef.current = el; }}
                style={{
                  position: "absolute",
                  top: (triggerRect.bottom + window.scrollY) + "px",
                  left: (triggerRect.left + window.scrollX) + "px",
                  width: triggerRect.width + "px",
                  maxHeight: 360,
                  backgroundColor: menuBg,
                  border: `1px solid ${menuBorder}`,
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  zIndex: 2147483647,
                  overflow: "auto",
                }}
              >
                {searchable ? (
                  <div style={{ padding: 8, borderBottom: `1px solid ${menuDivider}` }}>
                    <input
                      {...keepColorAttr}
                      style={{
                        height: 36,
                        width: "100%",
                        padding: 8,
                        boxSizing: "border-box",
                        fontSize: 13,
                        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        color: optionText,
                        ...(isDark
                          ? { backgroundColor: colors.background.surface, border: `1px solid ${menuBorder}`, borderRadius: 4 }
                          : {}),
                      }}
                      value={searchQuery}
                      onChange={(e: any) => setSearchQuery(e.target.value)}
                      placeholder={searchPlaceholder}
                    />
                  </div>
                ) : null}
                {filteredOptions.map((option) => {
                  const selected = multiSelect ? selectedValues.includes(option) : option === value;
                  const optionLabel = optionLabelMap?.[option] ?? option;
                  return (
                    <div
                      key={option}
                      onMouseDown={() => { /* use mouseDown to prevent blur before click */ handleOptionPress(option); }}
                      style={{
                        padding: "10px 12px",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: selected ? optionBgSelected : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      {multiSelect ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 16,
                            height: 16,
                            marginRight: 10,
                            borderRadius: 3,
                            border: selected ? "1px solid #006fce" : "1px solid #9aa4b2",
                            backgroundColor: selected ? "#006fce" : "transparent",
                            color: "#ffffff",
                            fontSize: 11,
                            lineHeight: "11px",
                            flexShrink: 0,
                          }}
                        >
                          {selected ? "\u2713" : ""}
                        </span>
                      ) : null}
                      <div
                        {...keepColorAttr}
                        style={{
                          fontSize: 13,
                          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          color: selected ? optionTextSelected : optionText,
                          fontWeight: selected ? 700 : 400,
                        }}
                      >
                        {optionLabel}
                      </div>
                    </div>
                  );
                })}
                {searchable && filteredOptions.length === 0 ? (
                  <div style={{ padding: 12 }}>
                    <div
                      {...keepColorAttr}
                      style={{
                        fontSize: 13,
                        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        color: isDark ? colors.text.secondary : "#818181",
                      }}
                    >
                      No matches found
                    </div>
                  </div>
                ) : null}
              </div>,
              document.body
            )
          ) : (
            <ScrollView style={[styles.selectMenu, darkMenu, { maxHeight: 300 }]} nestedScrollEnabled>
              {searchable ? (
                <View style={{ padding: 8, borderBottomWidth: 1, borderBottomColor: isDark ? colors.border.divider : "#ebebeb" }}>
                  <TextInput
                    style={[styles.input, isDark ? { backgroundColor: colors.background.surface, borderColor: colors.border.default, color: colors.text.primary } : null, { height: 36, marginBottom: 0 }]}
                    value={searchQuery}
                    onChangeText={setSearchQuery}
                    placeholder={searchPlaceholder}
                    placeholderTextColor={isDark ? colors.text.disabled : "#9aa4b2"}
                  />
                </View>
              ) : null}
              {filteredOptions.map((option) => {
                const selected = multiSelect ? selectedValues.includes(option) : option === value;
                const optionLabel = optionLabelMap?.[option] ?? option;
                return (
                  <Pressable
                    key={option}
                    onPress={() => handleOptionPress(option)}
                    style={({ pressed }) => [
                      styles.selectMenuItem,
                      darkMenuItem,
                      selected ? styles.selectMenuItemActive : null,
                      selected && isDark ? darkMenuItemActive : null,
                      pressed ? styles.selectMenuItemPressed : null,
                    ]}
                  >
                    {multiSelect ? (
                      <View style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
                        <MaterialIcons
                          name={selected ? "check-box" : "check-box-outline-blank"}
                          size={20}
                          color={selected ? (isDark ? colors.brand.primary : "#006fce") : (isDark ? colors.text.disabled : "#9aa4b2")}
                          style={{ marginRight: 12 }}
                        />
                        <Text style={[styles.selectMenuItemText, darkMenuItemText, selected ? styles.selectMenuItemTextActive : null, selected && isDark ? darkMenuItemTextActive : null]}>
                          {optionLabel}
                        </Text>
                      </View>
                    ) : (
                      <Text style={[styles.selectMenuItemText, darkMenuItemText, selected ? styles.selectMenuItemTextActive : null, selected && isDark ? darkMenuItemTextActive : null]}>
                        {optionLabel}
                      </Text>
                    )}
                  </Pressable>
                );
              })}
              {searchable && filteredOptions.length === 0 ? (
                <View style={{ paddingHorizontal: 12, paddingVertical: 10 }}>
                  <Text style={[styles.helperText, darkHelperText]}>No matches found</Text>
                </View>
              ) : null}
            </ScrollView>
          )
        ) : null}
      </View>

      {multiSelect && hasValue && !isOpen && (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {selectedValues.map((val) => (
            <View
              key={val}
              style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: isDark ? "#1B2A3D" : "#e8f0fe",
                borderRadius: 16,
                paddingVertical: 4,
                paddingLeft: 10,
                paddingRight: 6,
                borderWidth: 1,
                borderColor: isDark ? "#3D8BFF" : "#006fce",
              }}
            >
              <Text
                {...(isDark ? ({ dataSet: { lhKeepColor: "1" } } as any) : {})}
                style={{ fontSize: 13, color: isDark ? "#6AADFF" : "#006fce", marginRight: 4 }}
              >
                {optionLabelMap?.[val] ?? val}
              </Text>
              <Pressable
                onPress={() => {
                  const newSelected = selectedValues.filter((v) => v !== val);
                  onChange(newSelected.length ? JSON.stringify(newSelected) : "");
                }}
                style={({ pressed }: { pressed: boolean }) => ([
                  {
                    width: 18,
                    height: 18,
                    borderRadius: 9,
                    backgroundColor: pressed ? "#006fce" : (isDark ? "#2F4A6B" : "#c5d9f5"),
                    justifyContent: "center" as const,
                    alignItems: "center" as const,
                  },
                ])}
              >
                <Text
                  {...(isDark ? ({ dataSet: { lhKeepColor: "1" } } as any) : {})}
                  style={{ fontSize: 12, color: isDark ? "#FFFFFF" : "#006fce", lineHeight: 18, textAlign: "center" }}
                >
                  ×
                </Text>
              </Pressable>
            </View>
          ))}
        </View>
      )}

      {helperText && !isOpen ? <Text style={[styles.helperText, darkHelperText]}>{helperText}</Text> : null}
      {errorText && !isOpen ? <Text style={[styles.helperText, { color: "#c12c01" }]}>{errorText}</Text> : null}
    </View>
  );
};

export default SelectField;
